import { AlertTrigger } from '../entities/Alert';
import { GraphData } from '../entities/GraphData';
import { HealthParameterThreshold } from '../entities/HealthParameter';
import AlertSeverityEnum from '../enums/AlertSeverityEnum';
import HealthParameterEnum from '../enums/HealthParameterEnum';
import { MetricTypeEnum } from '../enums/MetricTypeEnum';
import formatNumber from './formatNumber';

type Component = 'systolic' | 'diastolic' | null;

export interface ChartSeries {
  label: string;
  /** Qual componente da régua limita esta série (só a PA tem dois). */
  component: Component;
  read: (item: GraphData) => number | null;
  /**
   * Um valor que com certeza está na faixa normal. É o que separa as regras "acima
   * do normal" das "abaixo do normal" sem hardcode de régua: a régua vem do back.
   */
  typical: number;
}

export interface AlertChartConfig {
  metricType: MetricTypeEnum;
  unit: string;
  series: ChartSeries[];
  /** Formato do eixo X. O sono é uma leitura por noite: a hora não informa nada. */
  hAxisFormat?: string;
}

export { formatNumber };

const numberOrNull = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * O parâmetro do alerta (`HealthParameterEnum`, nomenclatura do motor) traduzido
 * para a métrica do metrics service (`MetricTypeEnum`) e para as séries do gráfico.
 *
 * ⓘ `weight_loss` fica de fora: não existe métrica de peso no metrics service, e o
 * alerta dela é a variação numa janela, não uma leitura.
 */
const CONFIG: Partial<Record<string, AlertChartConfig>> = {
  [HealthParameterEnum.BLOOD_PRESSURE]: {
    metricType: MetricTypeEnum.BLOOD_PREASSURE,
    unit: 'mmHg',
    series: [
      {
        label: 'Sistólica',
        component: 'systolic',
        read: (item) => numberOrNull(item.hightPressure),
        typical: 110,
      },
      {
        label: 'Diastólica',
        component: 'diastolic',
        read: (item) => numberOrNull(item.lowPressure),
        typical: 70,
      },
    ],
  },
  [HealthParameterEnum.RESTING_HEART_RATE]: {
    metricType: MetricTypeEnum.HEART_BEAT_RATE,
    unit: 'bpm',
    series: [
      {
        label: 'Frequência cardíaca',
        component: null,
        read: (item) => numberOrNull(item.heartBeatRate),
        typical: 65,
      },
    ],
  },
  [HealthParameterEnum.BLOOD_OXYGEN]: {
    metricType: MetricTypeEnum.BLOOD_OXYGEN,
    unit: '%',
    series: [
      {
        label: 'SpO₂',
        component: null,
        read: (item) => numberOrNull(item.bloodOxygen),
        typical: 97,
      },
    ],
  },
  [HealthParameterEnum.BODY_TEMPERATURE]: {
    metricType: MetricTypeEnum.BODY_TEMPERATURE,
    unit: '°C',
    series: [
      {
        label: 'Temperatura',
        component: null,
        read: (item) => numberOrNull(item.bodyTemperature),
        typical: 36.5,
      },
    ],
  },
  [HealthParameterEnum.GLUCOSE]: {
    metricType: MetricTypeEnum.GLUCOSE,
    unit: 'mg/dL',
    series: [
      {
        label: 'Glicemia',
        component: null,
        read: (item) => numberOrNull(item.glucose),
        typical: 100,
      },
    ],
  },
  [HealthParameterEnum.SLEEP]: {
    metricType: MetricTypeEnum.SLEEP,
    unit: 'h',
    hAxisFormat: 'dd/MM',
    series: [
      {
        label: 'Sono',
        component: null,
        // `sleepTotalTime` vem em minutos; a régua fala em horas.
        read: (item) => {
          const minutes = numberOrNull(item.sleepTotalTime);

          return minutes === null ? null : Math.round((minutes / 60) * 10) / 10;
        },
        typical: 7.5,
      },
    ],
  },
};

export const alertChartConfig = (parameter: string): AlertChartConfig | null =>
  CONFIG[parameter] ?? null;

/**
 * Uma leitura do próprio alerta no formato do metrics service, para o gráfico cair
 * nela quando o metrics não devolve nada no período. O sono chega em horas no gatilho
 * e em minutos no metrics.
 */
export const triggerAsMetric = (
  trigger: AlertTrigger,
  config: AlertChartConfig,
): GraphData => {
  const base = {
    mensuramentDate: trigger.measuredAt,
    metricType: config.metricType,
  } as GraphData;

  switch (config.metricType) {
    case MetricTypeEnum.BLOOD_PREASSURE:
      return {
        ...base,
        hightPressure: trigger.systolic as number,
        lowPressure: trigger.diastolic as number,
      };
    case MetricTypeEnum.HEART_BEAT_RATE:
      return { ...base, heartBeatRate: trigger.value as number };
    case MetricTypeEnum.BLOOD_OXYGEN:
      return { ...base, bloodOxygen: trigger.value as number };
    case MetricTypeEnum.BODY_TEMPERATURE:
      return { ...base, bodyTemperature: trigger.value as number };
    case MetricTypeEnum.GLUCOSE:
      return { ...base, glucose: trigger.value as number };
    case MetricTypeEnum.SLEEP:
      return {
        ...base,
        sleepTotalTime:
          trigger.value === null
            ? (null as unknown as number)
            : trigger.value * 60,
      };
    default:
      return base;
  }
};

export interface LimitLine {
  severity: AlertSeverityEnum;
  side: 'upper' | 'lower';
  value: number;
  /** Ex.: `"Grave ≥ 140 (sistólica)"`. */
  label: string;
}

interface Interval {
  low: number;
  high: number;
  /** Como a ponta que encosta na faixa normal é escrita (`"≥ 140"`, `"< 70"`). */
  lowText: string;
  highText: string;
}

const toInterval = (t: HealthParameterThreshold): Interval => {
  switch (t.comparator) {
    case 'between':
      return {
        low: t.value,
        high: t.valueSecondary ?? t.value,
        lowText: `≥ ${formatNumber(t.value)}`,
        highText: `≤ ${formatNumber(t.valueSecondary ?? t.value)}`,
      };
    case 'gte':
      return {
        low: t.value,
        high: Infinity,
        lowText: `≥ ${formatNumber(t.value)}`,
        highText: '',
      };
    case 'gt':
      return {
        low: t.value,
        high: Infinity,
        lowText: `> ${formatNumber(t.value)}`,
        highText: '',
      };
    case 'lte':
      return {
        low: -Infinity,
        high: t.value,
        lowText: '',
        highText: `≤ ${formatNumber(t.value)}`,
      };
    default:
      return {
        low: -Infinity,
        high: t.value,
        lowText: '',
        highText: `< ${formatNumber(t.value)}`,
      };
  }
};

const COMPONENT_SUFFIX: Record<string, string> = {
  systolic: ' (sistólica)',
  diastolic: ' (diastólica)',
};

const SEVERITY_LABEL: Record<AlertSeverityEnum, string> = {
  [AlertSeverityEnum.MODERATE]: 'Moderado',
  [AlertSeverityEnum.SEVERE]: 'Grave',
};

/**
 * Os limites da faixa normal de uma série, por gravidade, lidos da régua **geral e
 * ativa** — a mesma que classifica a leitura.
 *
 * Por gravidade: o limite superior é a ponta mais baixa das faixas acima do normal, e
 * o inferior é a ponta mais alta das faixas abaixo dele. É o que dá conta das duas
 * faixas moderadas disjuntas da FC (81–119 **e** 40–49): cada uma vira um limite, em
 * vez de um intervalo único que engoliria os 50–80 normais.
 */
export const limitLines = (
  thresholds: HealthParameterThreshold[],
  parameter: string,
  series: ChartSeries,
): LimitLine[] => {
  const rules = thresholds.filter(
    (t) =>
      t.parameterSet === 'alerta' &&
      (t.careLine ?? null) === null &&
      t.active &&
      t.parameter === parameter &&
      (t.component ?? null) === series.component,
  );

  const suffix = series.component ? COMPONENT_SUFFIX[series.component] : '';

  return [AlertSeverityEnum.MODERATE, AlertSeverityEnum.SEVERE].flatMap(
    (severity) => {
      const intervals = rules
        .filter((t) => t.severity === severity)
        .map(toInterval);

      const above = intervals
        .filter((i) => i.low > series.typical)
        .sort((a, b) => a.low - b.low)[0];
      const below = intervals
        .filter((i) => i.high < series.typical)
        .sort((a, b) => b.high - a.high)[0];

      const lines: LimitLine[] = [];

      if (above) {
        lines.push({
          severity,
          side: 'upper',
          value: above.low,
          label: `${SEVERITY_LABEL[severity]} ${above.lowText}${suffix}`,
        });
      }

      if (below) {
        lines.push({
          severity,
          side: 'lower',
          value: below.high,
          label: `${SEVERITY_LABEL[severity]} ${below.highText}${suffix}`,
        });
      }

      return lines;
    },
  );
};
